import React, { useEffect } from 'react';
import { useAuth } from '../contexts';
import {
  TasksSummary,
  TaskFilters,
  TasksList,
  TaskEditModal,
  TaskCreateModal,
  TranscriptViewModal,
  useTasks,
  ActionItem
} from '../components/Tasks';

const TasksPageRefactored: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  
  const {
    // State
    tasks,
    loading,
    loadingMore,
    error,
    summary,
    pagination,
    filters,
    isSearching,
    editingTask,
    showTranscriptModal,
    showCreateModal,
    transcriptDetail,
    transcriptLoading,
    availableClients,

    // Actions
    setFilters,
    clearFilters,
    loadTasks,
    loadAvailableClients,
    handleLoadMore,
    handleQuickStatusUpdate,
    handleEditTask,
    handleSaveTask,
    handleCreateTask,
    handleViewTranscript,
    handleDeleteTask,
    setEditingTask,
    setShowTranscriptModal,
    setShowCreateModal,
    setTranscriptDetail,
  } = useTasks();

  // Load tasks and clients when authenticated
  useEffect(() => {
    if (isAuthenticated && !authLoading && user) {
      loadTasks();
      loadAvailableClients();
    }
  }, [isAuthenticated, authLoading, user]);

  // Loading states
  if (authLoading) {
    return (
      <div className="bg-gray-50 flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return (
      <div className="bg-gray-50 flex items-center justify-center py-20">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Authentication Required</h2>
          <p className="text-gray-600">Please log in to view your tasks.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => window.location.href = '/'}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Go to homepage"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m0 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1h3a1 1 0 001-1V10M9 21h6" />
                  </svg>
                </button>
                <h1 className="text-3xl font-bold text-gray-900">All Tasks</h1>
              </div>
              <p className="text-gray-600">Manage all your action items across all transcripts</p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            >
              ✚ Add Task
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        {summary && <TasksSummary summary={summary} />}

        {/* Filters */}
        <TaskFilters
          filters={filters}
          availableClients={availableClients || []}
          onFiltersChange={setFilters}
          onClearFilters={clearFilters}
        />

        {/* Tasks List */}
        <TasksList
          tasks={tasks}
          pagination={pagination}
          loading={loading}
          loadingMore={loadingMore}
          isSearching={isSearching}
          error={error}
          onQuickComplete={(taskId: string) => handleQuickStatusUpdate(taskId, 'completed')}
          onEdit={handleEditTask}
          onViewTranscript={handleViewTranscript}
          onDelete={handleDeleteTask}
          onLoadMore={handleLoadMore}
        />

        {/* Create Task Modal */}
        <TaskCreateModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreateTask}
        />

        {/* Edit Task Modal */}
        {editingTask && (
          <TaskEditModal
            task={editingTask}
            isOpen={!!editingTask}
            onClose={() => setEditingTask(null)}
            onSave={handleSaveTask}
            onTaskChange={setEditingTask}
          />
        )}

        {/* Transcript View Modal */}
        <TranscriptViewModal
          transcript={transcriptDetail}
          isOpen={showTranscriptModal}
          isLoading={transcriptLoading}
          onClose={() => {
            setShowTranscriptModal(false);
            setTranscriptDetail(null);
          }}
        />
      </div>
    </div>
  );
};

export default TasksPageRefactored; 